import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  Mail,
  Phone,
  MessageSquare,
  Trash2,
  RefreshCw,
  ExternalLink,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "@/lib/auth";
import { AdminTopbar } from "@/components/admin/AdminTopbar";
import { AdminTabs } from "@/components/admin/AdminTabs";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-SG", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const waLink = (phone, name) => {
  const clean = (phone || "").replace(/[^\d+]/g, "");
  const text = encodeURIComponent(
    `Hello ${name?.split(" ")[0] || "there"}, this is Noah+Rayn following up on your enquiry.`
  );
  return `https://wa.me/${clean.replace(/^\+/, "")}?text=${text}`;
};

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/contact?limit=500");
      setItems(data);
      if (data.length && !active) setActive(data[0]);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not load enquiries."
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      [it.name, it.email, it.phone, it.service, it.property_type, it.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, query]);

  const stats = useMemo(() => {
    const total = items.length;
    const last7 = items.filter((i) => {
      const t = new Date(i.created_at).getTime();
      return t > Date.now() - 7 * 24 * 3600 * 1000;
    }).length;
    const bto = items.filter((i) => i.property_type === "BTO").length;
    const resale = items.filter((i) =>
      String(i.property_type || "").toLowerCase().includes("resale")
    ).length;
    return { total, last7, bto, resale };
  }, [items]);

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await apiClient.delete(`/contact/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      if (active?.id === id) setActive(null);
      toast.success("Enquiry deleted.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Delete failed."
      );
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--nr-bg)]">
      <Toaster position="top-center" richColors />
      <AdminTopbar />

      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
        <AdminTabs />

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div>
            <p className="overline mb-3">Inbox</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-[color:var(--nr-ink)] tracking-tight">
              Enquiries
            </h1>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-5">
            {[
              { k: "Total", v: stats.total },
              { k: "Last 7d", v: stats.last7 },
              { k: "BTO", v: stats.bto },
              { k: "Resale", v: stats.resale },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`admin-stat-${s.k.toLowerCase().replace(/\s/g, "-")}`}
                className="rounded-2xl bg-[color:var(--nr-bg-soft)] border border-[color:var(--nr-line)] px-5 py-4 min-w-[100px]"
              >
                <div className="text-[0.65rem] uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">
                  {s.k}
                </div>
                <div className="font-serif-display text-3xl text-[color:var(--nr-ink)] mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Search + refresh */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 relative">
            <Search
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--nr-ink-soft)]"
            />
            <input
              data-testid="admin-search"
              type="search"
              placeholder="Search name, email, phone, service…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-full bg-[color:var(--nr-bg-soft)] border border-[color:var(--nr-line)] text-sm focus:outline-none focus:border-[color:var(--nr-accent)] transition-colors"
            />
          </div>
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[color:var(--nr-line)] text-xs uppercase tracking-[0.18em] hover:bg-[color:var(--nr-bg-soft)]"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-12 gap-6">
          {/* List */}
          <div className="lg:col-span-7 bg-[color:var(--nr-bg-soft)] border border-[color:var(--nr-line)] rounded-2xl overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-sm text-[color:var(--nr-ink-soft)]">
                Loading enquiries…
              </div>
            ) : filtered.length === 0 ? (
              <div
                data-testid="admin-empty"
                className="p-16 text-center"
              >
                <MessageSquare
                  size={36}
                  className="mx-auto text-[color:var(--nr-accent)]/50 mb-4"
                />
                <p className="font-serif-display text-2xl text-[color:var(--nr-ink)]">
                  {query ? "No matches." : "No enquiries yet."}
                </p>
                <p className="text-sm text-[color:var(--nr-ink-soft)] mt-2">
                  {query
                    ? "Try a different search term."
                    : "Once visitors submit the contact form, they'll appear here."}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-[color:var(--nr-line)]" data-testid="admin-list">
                {filtered.map((e) => (
                  <li key={e.id}>
                    <button
                      data-testid={`admin-row-${e.id}`}
                      onClick={() => setActive(e)}
                      className={`w-full text-left px-6 py-5 flex items-start gap-4 transition-colors ${
                        active?.id === e.id
                          ? "bg-[color:var(--nr-bg)]"
                          : "hover:bg-[color:var(--nr-bg)]/60"
                      }`}
                    >
                      <div className="w-11 h-11 rounded-full bg-[color:var(--nr-accent)]/10 text-[color:var(--nr-accent)] flex items-center justify-center font-serif-display text-lg shrink-0">
                        {(e.name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-medium text-[color:var(--nr-ink)] truncate">
                            {e.name}
                          </div>
                          <div className="text-xs text-[color:var(--nr-ink-soft)] shrink-0">
                            {fmtDate(e.created_at)}
                          </div>
                        </div>
                        <div className="text-sm text-[color:var(--nr-ink-soft)] mt-0.5 truncate">
                          {e.email} · {e.phone}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {e.property_type && (
                            <span className="text-[0.65rem] uppercase tracking-[0.16em] px-2 py-1 rounded-full bg-[color:var(--nr-bg-deep)] text-[color:var(--nr-ink)]">
                              {e.property_type}
                            </span>
                          )}
                          {e.service && (
                            <span className="text-[0.65rem] uppercase tracking-[0.16em] px-2 py-1 rounded-full bg-[color:var(--nr-accent)]/10 text-[color:var(--nr-accent)]">
                              {e.service}
                            </span>
                          )}
                          {e.budget && (
                            <span className="text-[0.65rem] uppercase tracking-[0.16em] px-2 py-1 rounded-full border border-[color:var(--nr-line)] text-[color:var(--nr-ink-soft)]">
                              {e.budget}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Detail */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 bg-[color:var(--nr-bg-soft)] border border-[color:var(--nr-line)] rounded-2xl p-7">
              {!active ? (
                <div className="text-center py-10">
                  <p className="font-serif-display text-2xl text-[color:var(--nr-ink)]">
                    Select an enquiry
                  </p>
                  <p className="text-sm text-[color:var(--nr-ink-soft)] mt-2">
                    Pick a row to see the full message and act on it.
                  </p>
                </div>
              ) : (
                <div data-testid="admin-detail">
                  <p className="overline mb-3">Enquiry</p>
                  <h3 className="font-serif-display text-3xl text-[color:var(--nr-ink)] leading-tight">
                    {active.name}
                  </h3>
                  <p className="text-sm text-[color:var(--nr-ink-soft)] mt-1">
                    Received {fmtDate(active.created_at)}
                  </p>

                  <div className="mt-7 space-y-4">
                    <a
                      data-testid="admin-detail-email"
                      href={`mailto:${active.email}`}
                      className="flex items-center gap-3 group"
                    >
                      <div className="w-10 h-10 rounded-full bg-[color:var(--nr-bg)] flex items-center justify-center ring-1 ring-[color:var(--nr-line)] group-hover:bg-[color:var(--nr-accent)] transition-colors">
                        <Mail
                          size={15}
                          className="text-[color:var(--nr-accent)] group-hover:text-white transition-colors"
                        />
                      </div>
                      <div className="text-sm">
                        <div className="text-[0.65rem] uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">
                          Email
                        </div>
                        <div className="text-[color:var(--nr-ink)]">
                          {active.email}
                        </div>
                      </div>
                    </a>
                    <a
                      data-testid="admin-detail-whatsapp"
                      href={waLink(active.phone, active.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 group"
                    >
                      <div className="w-10 h-10 rounded-full bg-[color:var(--nr-bg)] flex items-center justify-center ring-1 ring-[color:var(--nr-line)] group-hover:bg-[#25D366] transition-colors">
                        <Phone
                          size={15}
                          className="text-[color:var(--nr-accent)] group-hover:text-white transition-colors"
                        />
                      </div>
                      <div className="text-sm">
                        <div className="text-[0.65rem] uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">
                          WhatsApp / Phone
                        </div>
                        <div className="text-[color:var(--nr-ink)]">
                          {active.phone}
                        </div>
                      </div>
                      <ExternalLink
                        size={12}
                        className="ml-auto text-[color:var(--nr-ink-soft)]"
                      />
                    </a>
                  </div>

                  <div className="nr-rule my-6" />

                  <dl className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                    <div>
                      <dt className="text-[0.65rem] uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">
                        Property
                      </dt>
                      <dd className="text-[color:var(--nr-ink)] mt-1">
                        {active.property_type || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[0.65rem] uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">
                        Service
                      </dt>
                      <dd className="text-[color:var(--nr-ink)] mt-1">
                        {active.service || "—"}
                      </dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-[0.65rem] uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">
                        Budget
                      </dt>
                      <dd className="text-[color:var(--nr-ink)] mt-1">
                        {active.budget || "—"}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-6">
                    <div className="text-[0.65rem] uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)] mb-2">
                      Message
                    </div>
                    <div
                      data-testid="admin-detail-message"
                      className="bg-[color:var(--nr-bg)] border border-[color:var(--nr-line)] rounded-xl p-4 text-sm leading-relaxed text-[color:var(--nr-ink)] whitespace-pre-wrap min-h-[80px]"
                    >
                      {active.message || (
                        <span className="text-[color:var(--nr-ink-soft)] italic">
                          No message provided.
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-7 flex items-center gap-3">
                    <a
                      data-testid="admin-detail-reply"
                      href={waLink(active.phone, active.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary rounded-full px-6 py-3 text-xs uppercase tracking-[0.18em] font-semibold inline-flex items-center gap-2"
                    >
                      <Phone size={14} /> Reply via WhatsApp
                    </a>
                    <button
                      data-testid="admin-detail-delete"
                      onClick={() => remove(active.id)}
                      className="ml-auto inline-flex items-center gap-2 px-4 py-3 rounded-full border border-[color:var(--nr-line)] text-xs uppercase tracking-[0.18em] text-[#A33A1F] hover:bg-[#FBE9E0] hover:border-[#E8C0AE]"
                      aria-label="Delete enquiry"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
